/**
 * Модель данных ролевой модели (DAC) — фронтовая часть.
 * Источник истины — backend `access/enums.py` и контракты `access/serializers.py`.
 *
 * Матрица эффективных прав приходит с бэка ПЛОСКИМ словарём `{"section.action": bool}`
 * (разделитель — точка, см. `services.get_effective_matrix`). Ключи строим через `permKey`.
 */

/** 5 основных независимых действий. */
export type Action = 'view' | 'edit' | 'create' | 'delete' | 'export';

/** Служебные права (не привязаны к разделу). */
export type ServiceAction = 'manage_access' | 'delete_project' | 'transfer_ownership';

/** 3 состояния ячейки матрицы. */
export type CellState = 'granted' | 'denied' | 'not_applicable';

/** Проектные роли (СУПЕРАДМИН — отдельная ось is_superuser, сюда не входит). */
export type ProjectRole = 'owner' | 'admin' | 'guest';

/** Статус участника (membership). */
export type MembershipStatus = 'active' | 'suspended';

/** Статус приглашения участника. */
export type InviteStatus = 'accepted' | 'pending' | 'expired' | 'revoked';

/** 18 защищаемых разделов проекта. */
export type ProjectSection =
  | 'project'
  | 'scenarios'
  | 'variants'
  | 'project_params'
  | 'basic_data'
  | 'tax_settings'
  | 'discount_rate'
  | 'capex'
  | 'sales_revenue'
  | 'costs'
  | 'financing'
  | 'chat'
  | 'calc_result'
  | 'charts_reports'
  | 'monitoring'
  | 'risk_assessment'
  | 'analytics'
  | 'portfolio_modeling';

/** Плоская матрица эффективных прав: `{"section.action": boolean}`. */
export type PermissionMatrix = Record<string, boolean>;

/** Один элемент матрицы пресета/override (плоский список с бэка). */
export interface IPermissionItem {
  section: ProjectSection;
  action: Action;
  state: CellState;
}

/** Краткое представление пресета (в участнике/инвайте). */
export interface IPresetMini {
  id: number;
  name: string;
  is_system: boolean;
}

/** Участник проекта (MemberSerializer). */
export interface IProjectMember {
  id: number;
  user_id: number;
  email: string;
  name: string;
  role: ProjectRole;
  preset: IPresetMini | null;
  status: MembershipStatus;
  invite_status: InviteStatus;
  effective_permissions: PermissionMatrix;
  created_at: string;
  updated_at: string;
}

/** Приглашение (InvitationSerializer). */
export interface IInvitation {
  id: number;
  project_id: number;
  project_name: string;
  email: string;
  name: string;
  role: ProjectRole;
  preset: IPresetMini | null;
  status: InviteStatus;
  expires_at: string | null;
  invited_by_email: string | null;
  created_at: string;
  updated_at: string;
}

/** Кастом/системная роль-пресет (RolePresetSerializer). */
export interface IRolePreset {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  is_mine: boolean;
  permissions: IPermissionItem[];
  created_at: string;
  updated_at: string;
}

/** Доступ участника в другом проекте (OtherProjectMembershipSerializer). */
export interface IOtherProjectMembership {
  id: number;
  project_id: number;
  project_name: string;
  role: ProjectRole;
  preset: IPresetMini | null;
  status: MembershipStatus;
  updated_at: string;
}

/** Системная роль в каталоге /access/roles/ (owner/admin/guest). */
export interface ICatalogSystemRole {
  key: ProjectRole;
  label: string;
  permissions: IPermissionItem[];
}

/** Пресет в каталоге /access/roles/. */
export interface ICatalogPreset {
  id: number;
  name: string;
  description: string;
  permissions: IPermissionItem[];
}

/** Ответ GET /access/roles/ — справочник для «Сравнить все роли» (F8). */
export interface IRolesCatalog {
  system_roles: ICatalogSystemRole[];
  system_presets: ICatalogPreset[];
  custom_presets: ICatalogPreset[];
  section_actions: Record<string, string[]>;
}

/** Ответ /my-permissions/ и /members/{mid}/permissions/. */
export interface IMyPermissions {
  project_id: number;
  permissions: PermissionMatrix;
}

export interface IMemberPermissions {
  membership_id: number;
  user_id: number;
  role: ProjectRole;
  permissions: PermissionMatrix;
}

// ──────────────────────── Константы / реестры (зеркало бэка) ────────────────────────

/** Все 5 действий в каноническом порядке V E C D X. */
export const ALL_ACTIONS: Action[] = ['view', 'edit', 'create', 'delete', 'export'];

/**
 * Реестр применимости действий к разделам (зеркало `enums.SECTION_ACTIONS`).
 * Пары, которых нет в списке секции, — `not_applicable` (в UI рисуем disabled 🚫).
 */
export const SECTION_ACTIONS: Record<ProjectSection, Action[]> = {
  project: ['view', 'edit', 'delete'],
  scenarios: ['view', 'edit', 'create', 'delete'],
  variants: ['view', 'edit', 'create', 'delete'],
  project_params: ['view', 'edit'],
  basic_data: ['view', 'edit', 'create', 'delete'],
  tax_settings: ['view', 'edit', 'create', 'delete'],
  discount_rate: ['view', 'edit', 'create', 'delete'],
  capex: ['view', 'edit', 'create', 'delete'],
  sales_revenue: ['view', 'edit', 'create', 'delete'],
  costs: ['view', 'edit', 'create', 'delete'],
  financing: ['view', 'edit', 'create', 'delete'],
  chat: ['view', 'edit', 'create', 'delete'],
  calc_result: ['view', 'create', 'export'],
  charts_reports: ['view', 'edit', 'create', 'delete', 'export'],
  monitoring: ['view', 'edit', 'create', 'delete', 'export'],
  risk_assessment: ['view', 'edit', 'create', 'delete'],
  analytics: ['view', 'edit', 'create', 'delete', 'export'],
  portfolio_modeling: ['view', 'edit', 'create', 'delete'],
};

/** Человекочитаемые названия разделов (для UI матриц). */
export const SECTION_LABELS: Record<ProjectSection, string> = {
  project: 'Весь проект',
  scenarios: 'Сценарии',
  variants: 'Варианты',
  project_params: 'Параметры проекта',
  basic_data: 'Основные данные',
  tax_settings: 'Настройки налоговых расчётов',
  discount_rate: 'Ставка дисконтирования',
  capex: 'Капитальные расходы',
  sales_revenue: 'Продажи и расчёт выручки',
  costs: 'Расчёт затрат',
  financing: 'Источники финансирования',
  chat: 'Чат проекта',
  calc_result: 'Результат расчёта',
  charts_reports: 'Графики и отчёты',
  monitoring: 'Мониторинг',
  risk_assessment: 'Оценка рисков',
  analytics: 'Аналитика и отчётность',
  portfolio_modeling: 'Портфельное моделирование',
};

/** Человекочитаемые названия действий. */
export const ACTION_LABELS: Record<Action, string> = {
  view: 'Просмотр',
  edit: 'Редактирование',
  create: 'Создание',
  delete: 'Удаление',
  export: 'Экспорт',
};

/** Названия ролей. */
export const ROLE_LABELS: Record<ProjectRole, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  guest: 'Гость',
};

/**
 * Группировка разделов для UI (мастер-ТЗ 3.3). Не влияет на enforcement —
 * только заголовки в матрицах редактора прав.
 */
export interface ISectionGroup {
  title: string;
  sections: ProjectSection[];
}

export const SECTION_GROUPS: ISectionGroup[] = [
  {
    title: 'Весь проект',
    sections: ['project', 'scenarios', 'variants'],
  },
  {
    title: 'Параметры проекта',
    sections: [
      'project_params',
      'basic_data',
      'tax_settings',
      'discount_rate',
      'capex',
      'sales_revenue',
      'costs',
      'financing',
    ],
  },
  {
    title: 'Дополнительные функции',
    sections: ['chat', 'calc_result', 'charts_reports', 'monitoring', 'risk_assessment'],
  },
];

/** Дефолтные роли для дропдауна приглашения (системные роли + кастом подмешиваются отдельно). */
export const DEFAULT_INVITE_ROLES: { value: ProjectRole; label: string }[] = [
  { value: 'admin', label: 'Администратор' },
  { value: 'guest', label: 'Гость' },
];

/** Ключ плоской матрицы для пары (section, action). */
export function permKey(section: ProjectSection, action: Action): string {
  return `${section}.${action}`;
}

/** Применимо ли действие к разделу (есть ли функционал). */
export function isApplicable(section: ProjectSection, action: Action): boolean {
  return SECTION_ACTIONS[section]?.includes(action) ?? false;
}

/**
 * Состояние ячейки для матрицы из плоской bool-карты эффективных прав:
 * not_applicable, если действие неприменимо; иначе granted/denied по карте.
 */
export function cellStateFromMatrix(
  matrix: PermissionMatrix,
  section: ProjectSection,
  action: Action,
): CellState {
  if (!isApplicable(section, action)) return 'not_applicable';
  return matrix[permKey(section, action)] ? 'granted' : 'denied';
}

/** Карта состояний ячеек `{"section.action": CellState}`. */
export type CellMatrix = Record<string, CellState>;

/** Полная карта состояний из плоской bool-матрицы эффективных прав. */
export function cellMatrixFromBool(matrix: PermissionMatrix): CellMatrix {
  const out: CellMatrix = {};
  (Object.keys(SECTION_ACTIONS) as ProjectSection[]).forEach((section) => {
    ALL_ACTIONS.forEach((action) => {
      out[permKey(section, action)] = cellStateFromMatrix(matrix, section, action);
    });
  });
  return out;
}

/** Карта состояний из плоского списка прав пресета (неуказанные применимые → denied). */
export function cellMatrixFromItems(items: IPermissionItem[]): CellMatrix {
  const out: CellMatrix = {};
  (Object.keys(SECTION_ACTIONS) as ProjectSection[]).forEach((section) => {
    ALL_ACTIONS.forEach((action) => {
      out[permKey(section, action)] = isApplicable(section, action) ? 'denied' : 'not_applicable';
    });
  });
  items.forEach((it) => {
    if (isApplicable(it.section, it.action)) out[permKey(it.section, it.action)] = it.state;
  });
  return out;
}

/** Дефолтная карта роли ГОСТЬ: view granted на применимых, остальное denied. */
export function guestDefaultCellMatrix(): CellMatrix {
  const out: CellMatrix = {};
  (Object.keys(SECTION_ACTIONS) as ProjectSection[]).forEach((section) => {
    ALL_ACTIONS.forEach((action) => {
      if (!isApplicable(section, action)) out[permKey(section, action)] = 'not_applicable';
      else out[permKey(section, action)] = action === 'view' ? 'granted' : 'denied';
    });
  });
  return out;
}

/** Дефолтная карта роли ВЛАДЕЛЕЦ/АДМИН: всё granted (admin — кроме project.delete). */
export function fullRoleCellMatrix(role: 'owner' | 'admin'): CellMatrix {
  const out: CellMatrix = {};
  (Object.keys(SECTION_ACTIONS) as ProjectSection[]).forEach((section) => {
    ALL_ACTIONS.forEach((action) => {
      if (!isApplicable(section, action)) out[permKey(section, action)] = 'not_applicable';
      else out[permKey(section, action)] = 'granted';
    });
  });
  if (role === 'admin') out[permKey('project', 'delete')] = 'denied';
  return out;
}

/** Преобразовать карту состояний в плоский список применимых прав (для PUT). */
export function cellMatrixToItems(cm: CellMatrix): IPermissionItem[] {
  const items: IPermissionItem[] = [];
  (Object.keys(SECTION_ACTIONS) as ProjectSection[]).forEach((section) => {
    SECTION_ACTIONS[section].forEach((action) => {
      const state = cm[permKey(section, action)];
      if (state === 'granted' || state === 'denied') items.push({ section, action, state });
    });
  });
  return items;
}
